import { useMemo, useState } from 'react';

import { Grid } from '@mui/material';
import { PartnerType } from '@prisma/client';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useQuery } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { PARTNER_TABLE_HEADERS } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { GET_PARTNERS } from 'src/graphql/queries/partners.query';
import { GET_SUBPARTNERS } from 'src/graphql/queries/subpartners.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { enumToCamelCase } from 'src/lib/formatters';
import {
    filteredPartnersTableData,
    partnersFilterOptions,
    updateTableFilters,
} from 'src/utils/tableUtils';

const PartnersPage = () => {
    const [tableFilters, setTableFilters] = useState({});
    // the current user
    const { user, isAdmin, isPartner } = useAuthContext();

    // GraphQL query to fetch list of top level partners
    const { data, loading } = useQuery(GET_PARTNERS, {
        fetchPolicy: 'cache-and-network',
        onError: (err) => showErrorToast(err.message),
    });
    // GraphQL query to fetch list of subpartners
    const { data: allSubPartners, loading: fetchingSubPartners } = useQuery(GET_SUBPARTNERS, {
        skip: !isAdmin,
        fetchPolicy: 'cache-and-network',
        onError: (err) => showErrorToast(err.message),
    });

    const updatedTableData = useMemo(() => {
        const partners = data?.partners ?? [];
        const partnersOfPartners = allSubPartners?.partnerOfPartners ?? [];
        let partnersData;
        if (isPartner) {
            // Filter partners by matching business ID
            const filteredPartners = partners?.filter((partner) => {
                if (!partner?.business || !user.business) return false;
                return partner.business.id === user.business.id;
            });

            if (!filteredPartners?.length) return [];

            partnersData = filteredPartners[0]?.partnerOfPartner?.map((pop) => {
                return {
                    ...pop,
                    name: pop?.business?.legalName,
                    type: enumToCamelCase(PartnerType.PARTNER_OF_PARTNER),
                    primaryContactName: `${pop.firstName} ${pop.lastName}`,
                    //TODO: to be added
                    topLevelPartner: filteredPartners[0]?.business?.legalName,
                    status: <StatusChip status={pop.business?.status} type="partner" />,
                };
            });
        } else {
            // User is an admin type in this case so show all top level partners and sub partners
            const topLevelPartners = partners.map((partner) => {
                return {
                    ...partner,
                    name: partner.business?.legalName,
                    type: enumToCamelCase(PartnerType.PARTNER),
                    primaryContactName: `${partner?.firstName} ${partner?.lastName}`,
                    topLevelPartner: '-',
                    status: partner.business?.status,
                };
            });
            const subPartners =
                partnersOfPartners?.map((subPartner) => {
                    return {
                        ...subPartner,
                        name: subPartner.business?.legalName,
                        type: enumToCamelCase(PartnerType.PARTNER_OF_PARTNER),
                        primaryContactName: `${subPartner?.firstName} ${subPartner?.lastName}`,
                        topLevelPartner: subPartner?.partner?.business?.legalName,
                        status: subPartner.business?.status,
                    };
                }) || [];

            partnersData = [...topLevelPartners, ...subPartners];
        }
        return partnersData;
    }, [isPartner, user?.business, data?.partners, allSubPartners?.partnersOfpartners]);

    const filterOptions = () => partnersFilterOptions(updatedTableData);

    // set the table data
    const tableData = useMemo(() => {
        return filteredPartnersTableData(updatedTableData, tableFilters).map((partner) => ({
            ...partner,
            status: <StatusChip status={partner.business?.status} type="partner" />,
        }));
    }, [updatedTableData, tableFilters]);

    // Navigate to add new partner page
    const addNewPartner = () => {
        navigate('/partner/add');
    };

    // Navigate to view partner by id page
    const handleViewPartner = ({ id, type }: { id: number; type: PartnerType }) => {
        if (type === enumToCamelCase(PartnerType.PARTNER)) {
            navigate(`/partner/${id}`);
        } else if (type === enumToCamelCase(PartnerType.PARTNER_OF_PARTNER)) {
            navigate(`/partner/${id}?partnerOfPartner=true`);
        }
    };

    return (
        <PageContent
            heading="Partners"
            subHeading="Add, View, Remove and Update NPI Partners as necessary"
        >
            <MetaTags title="Partners" description="Partners page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {/* Display loader if data is still fetching */}
                    {loading || !data || fetchingSubPartners ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Partner"
                            data={tableData}
                            headers={PARTNER_TABLE_HEADERS}
                            title="Partners List"
                            addRecordCallBack={addNewPartner}
                            handleViewRecord={handleViewPartner}
                            //TODO: add the delete functionality
                            deleteRecordCallBack={() => {}}
                            enableFilter
                            enableExport
                            filterOptions={filterOptions()}
                            updateTableFilters={(filter: string, newFilterValue: string) =>
                                setTableFilters((prevFilters) =>
                                    updateTableFilters(prevFilters, newFilterValue, filter)
                                )
                            }
                        />
                    )}
                </Grid>
            </Grid>
        </PageContent>
    );
};

export default PartnersPage;
